// =============================================================================
// MeuDriver — Cancellation Policy Page
// =============================================================================

import { Link } from 'react-router-dom'
import { Shield, Clock, AlertTriangle, XCircle, ArrowLeft, Info, HelpCircle } from 'lucide-react'
import { CancellationPolicy } from '@/components/shared/CancellationPolicy'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'

const FAQS = [
  {
    q: 'Como solicito o cancelamento de uma aula?',
    a: 'Acesse a página de detalhes da aula no seu painel e clique em "Cancelar aula". O reembolso é calculado automaticamente com base no horário do cancelamento.',
  },
  {
    q: 'Em quanto tempo recebo o reembolso?',
    a: 'Reembolsos são processados em até 5 dias úteis diretamente no método de pagamento utilizado na compra.',
  },
  {
    q: 'O que acontece se o instrutor cancelar?',
    a: 'Se o instrutor cancelar a aula, você recebe reembolso integral independentemente do prazo, além de uma notificação imediata.',
  },
  {
    q: 'Posso reagendar em vez de cancelar?',
    a: 'Sim. Entre em contato com o instrutor pelo chat da plataforma para combinar um novo horário. Reagendamentos acordados entre as partes não geram cobranças adicionais.',
  },
  {
    q: 'O que é considerado "não comparecimento" (no-show)?',
    a: 'Quando o aluno não aparece no local combinado sem cancelamento prévio. Neste caso não há reembolso.',
  },
]

export default function CancellationPolicyPage() {
  return (
    <div className="max-w-3xl mx-auto px-4 py-10 space-y-8">

      {/* Back */}
      <Button variant="ghost" size="sm" asChild className="-ml-2">
        <Link to="/ajuda">
          <ArrowLeft className="h-4 w-4 mr-1.5" />
          Voltar para Ajuda
        </Link>
      </Button>

      {/* Hero */}
      <div className="flex items-start gap-4">
        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-brand-50 border border-brand-100">
          <Shield className="h-6 w-6 text-brand-600" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Política de cancelamento</h1>
          <p className="text-muted-foreground mt-1">
            Entenda como funcionam os reembolsos e cancelamentos na plataforma MeuDriver.
          </p>
        </div>
      </div>

      {/* Tiers */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Clock className="h-4 w-4 text-primary" />
            Tabela de reembolso por prazo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <CancellationPolicy />
        </CardContent>
      </Card>

      {/* How it works */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Info className="h-4 w-4 text-primary" />
            Como funciona o processo
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ol className="space-y-4">
            {[
              { step: '1', title: 'Solicite o cancelamento', desc: 'Acesse o detalhe da aula no seu painel e clique em "Cancelar aula". A hora do pedido determina a faixa de reembolso.' },
              { step: '2', title: 'Confirmação automática', desc: 'O sistema confirma o cancelamento instantaneamente e calcula o valor a ser devolvido com base na política vigente.' },
              { step: '3', title: 'Notificação ao instrutor', desc: 'O instrutor é notificado imediatamente, podendo liberar o horário para outro aluno.' },
              { step: '4', title: 'Reembolso processado', desc: 'O valor é devolvido ao método de pagamento original em até 5 dias úteis.' },
            ].map(({ step, title, desc }) => (
              <li key={step} className="flex gap-4">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-brand-50 border border-brand-200 text-xs font-bold text-brand-700">
                  {step}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">{title}</p>
                  <p className="text-sm text-muted-foreground mt-0.5">{desc}</p>
                </div>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      {/* Special cases */}
      <Card className="border-amber-200 bg-amber-50/40">
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2 text-amber-800">
            <AlertTriangle className="h-4 w-4" />
            Casos especiais
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3 text-sm text-amber-900">
          <div className="flex gap-3">
            <XCircle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
            <p><strong>Cancelamento pelo instrutor:</strong> Reembolso integral sempre, independente do prazo. O aluno também recebe uma notificação imediata.</p>
          </div>
          <div className="flex gap-3">
            <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
            <p><strong>Força maior:</strong> Em situações documentadas (emergências médicas, acidentes), entre em contato com o suporte para análise individual.</p>
          </div>
          <div className="flex gap-3">
            <Shield className="h-4 w-4 shrink-0 mt-0.5 text-amber-600" />
            <p><strong>Disputas:</strong> Caso haja divergência, a equipe MeuDriver analisa e decide com base nas evidências apresentadas por ambas as partes.</p>
          </div>
        </CardContent>
      </Card>

      {/* FAQs */}
      <div>
        <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2 mb-4">
          <HelpCircle className="h-5 w-5 text-primary" />
          Perguntas frequentes
        </h2>
        <div className="space-y-3">
          {FAQS.map(({ q, a }) => (
            <details
              key={q}
              className="group rounded-xl border border-gray-100 bg-white px-4 py-3 open:shadow-sm transition-all"
            >
              <summary className="flex cursor-pointer items-center justify-between gap-2 text-sm font-medium text-gray-800 list-none">
                {q}
                <span className="text-muted-foreground text-lg leading-none group-open:rotate-45 transition-transform duration-150">+</span>
              </summary>
              <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a}</p>
            </details>
          ))}
        </div>
      </div>

      {/* CTA */}
      <div className="rounded-xl border border-brand-100 bg-brand-50/50 px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
        <div>
          <p className="text-sm font-semibold text-brand-800">Ainda tem dúvidas?</p>
          <p className="text-xs text-brand-700 mt-0.5">Nossa equipe de suporte está pronta para ajudar.</p>
        </div>
        <Button asChild size="sm" className="shrink-0">
          <Link to="/ajuda">Acessar central de ajuda</Link>
        </Button>
      </div>

    </div>
  )
}
